import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "316",
  "slug": "longest-rivers-in-the-world-ranked",
  "title": "The 10 Longest Rivers in the World, Ranked (The Top Spot Is Closer Than You Think)",
  "excerpt": "Is the Nile longer than the Amazon, or the other way around? The answer is more contested than most people realise. Here are the ten longest rivers on Earth, ranked, with the geography, hydrology, and ongoing debates behind each.",
  "tldr": "The Nile is officially the longest river at about 6,650 km, narrowly edging out the Amazon at 6,400 km — though Brazilian expeditions argue the Amazon's true source pushes it past 7,000 km. The next eight are the Yangtze, Mississippi-Missouri, Yenisei, Yellow, Ob-Irtysh, Paraná, Congo, and Amur.",
  "date": "2026-05-01",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&q=80",
  "readTime": "7 min",
  "content": [
    {
      "type": "paragraph",
      "text": "If you ask ten geographers which is the longest river in the world, you might get eight answers of \"the Nile,\" one \"the Amazon,\" and one \"it depends how you measure it.\" The honest answer is the third one. The longest rivers on Earth are surprisingly close to each other in length, the official numbers depend on how you define a river's source and mouth, and the rankings have changed several times in the past 20 years as expeditions have pushed further into headwater regions."
    },
    {
      "type": "paragraph",
      "text": "Here is the most defensible current list of the ten longest rivers on Earth, ranked by length, with the geography and the debates behind each."
    },
    {
      "type": "heading",
      "text": "1. The Nile — Approximately 6,650 km"
    },
    {
      "type": "paragraph",
      "text": "The Nile is traditionally considered the longest river in the world. It flows north from the East African Great Lakes region through Uganda, South Sudan, Sudan, and Egypt to the Mediterranean Sea. The conventional source is Lake Victoria, but expeditions have traced the Nile's longest tributary further upstream into Burundi or Rwanda — the precise source is still debated, and that debate is part of why its length is given as a range rather than an exact number."
    },
    {
      "type": "paragraph",
      "text": "From orbit, the Nile is one of the most recognisable rivers on Earth. The narrow green strip of irrigation along its banks, cutting through the Sahara, produces the sharpest land-use boundary visible from space — the contrast between the lush Nile Valley and the surrounding desert is absolute."
    },
    {
      "type": "heading",
      "text": "2. The Amazon — Approximately 6,400 km (or 7,000+ km, depending on who you ask)"
    },
    {
      "type": "paragraph",
      "text": "The Amazon is the second-longest river by conventional measurement, but a 2007 Brazilian expedition argued it should be considered the longest — they traced the source up the Apurímac and Ucayali rivers in the Peruvian Andes and produced a measurement of around 7,062 kilometres. The expedition's findings are still debated, and most international references continue to list the Nile as longer. But the Amazon is unambiguously the largest river on Earth by volume — it carries roughly five times as much water as the Congo (the second-largest by volume) and about 60 times more than the Nile."
    },
    {
      "type": "paragraph",
      "text": "From orbit, the Amazon is unmistakable. The dark, sinuous main channel cuts across South America with a width that reaches over 50 kilometres in the wet season near its mouth. The river is so large that the freshwater plume from its outflow is visible in satellite imagery hundreds of kilometres offshore in the Atlantic."
    },
    {
      "type": "heading",
      "text": "3. The Yangtze (Chang Jiang) — Approximately 6,300 km"
    },
    {
      "type": "paragraph",
      "text": "The Yangtze is the longest river in Asia and the third-longest in the world. It rises in the Tibetan plateau and flows generally east across China to the East China Sea at Shanghai. It is the most economically important river on Earth — the Yangtze basin contains over 400 million people, the world's largest hydropower facility (the Three Gorges Dam), and a significant share of global agricultural and industrial output."
    },
    {
      "type": "paragraph",
      "text": "From satellite imagery, the Yangtze is identifiable by its dense urban corridor — a chain of megacities including Chongqing, Wuhan, Nanjing, and Shanghai sits along the river, producing a continuous urban footprint visible from orbit. The Three Gorges Dam itself is one of the most clearly visible human-engineering features on Earth."
    },
    {
      "type": "heading",
      "text": "4. The Mississippi-Missouri System — Approximately 6,275 km"
    },
    {
      "type": "paragraph",
      "text": "The Mississippi-Missouri system is the longest river in North America. The Missouri rises in the Rocky Mountains of Montana, flows east-southeast across the Great Plains, and joins the Mississippi at St. Louis. From there, the combined river continues south to the Gulf of Mexico through the bird-foot delta of southern Louisiana. The Missouri's contribution makes the combined system one of the longest on Earth, even though the Mississippi alone is shorter."
    },
    {
      "type": "paragraph",
      "text": "The Mississippi Delta is one of the most distinctive river mouths visible from orbit — its bird-foot shape, formed by sediment deposition outpacing wave erosion, is found in only a handful of places on Earth."
    },
    {
      "type": "heading",
      "text": "5. The Yenisei-Angara System — Approximately 5,539 km"
    },
    {
      "type": "paragraph",
      "text": "The Yenisei drains a vast area of central Siberia, with its main tributary the Angara flowing out of Lake Baikal. From orbit, the Yenisei is one of the most striking rivers on Earth in winter — its frozen ice cover stretches over a thousand kilometres, and the spring thaw produces some of the most dramatic ice-jam flooding on the planet."
    },
    {
      "type": "heading",
      "text": "6. The Yellow River (Huang He) — Approximately 5,464 km"
    },
    {
      "type": "paragraph",
      "text": "The Yellow River is China's second-longest river and one of the most heavily silt-laden waterways on Earth — its name comes from the bright yellow colour of the loess sediment it carries. The Yellow River drains the Loess Plateau, where centuries of intensive agriculture have produced one of the most distinctive landscapes in China: vast terraced hills cut by deep gullies, all in shades of pale yellow-brown."
    },
    {
      "type": "heading",
      "text": "7. The Ob-Irtysh System — Approximately 5,410 km"
    },
    {
      "type": "paragraph",
      "text": "The Ob drains western Siberia from the Altai Mountains to the Arctic Ocean. Its tributary the Irtysh rises in the Mongolian Altai. Together they form one of the longest river systems on Earth, draining one of the largest watersheds. The Ob basin's western part contains some of the world's largest peatlands and is one of the major sources of Arctic freshwater discharge."
    },
    {
      "type": "heading",
      "text": "8. The Paraná — Approximately 4,880 km"
    },
    {
      "type": "paragraph",
      "text": "The Paraná is the second-longest river in South America, draining the Pampas of Argentina, Paraguay, Brazil, and Uruguay. It flows into the Río de la Plata estuary between Argentina and Uruguay — one of the widest river mouths in the world, with a maximum width of over 220 kilometres. From orbit, the Río de la Plata appears almost as a small sea, brown with sediment, between two distinct countries."
    },
    {
      "type": "heading",
      "text": "9. The Congo — Approximately 4,700 km"
    },
    {
      "type": "paragraph",
      "text": "The Congo is the second-largest river by volume in the world and the deepest river on Earth, with measured depths exceeding 220 metres in places. It flows in a vast arc through Central Africa, twice crossing the equator — the only major river in the world to do so. The Congo's rainforest basin is the second-largest tropical rainforest on Earth after the Amazon, and the river's discharge into the Atlantic is so powerful it has carved an underwater canyon over 800 kilometres long."
    },
    {
      "type": "heading",
      "text": "10. The Amur-Argun System — Approximately 4,444 km"
    },
    {
      "type": "paragraph",
      "text": "The Amur forms much of the border between Russia and northeastern China. It rises in the mountains of Mongolia and flows generally east-northeast to the Sea of Okhotsk. It is the longest river in the world that is not dammed along its main stem — a rare distinction for a major waterway in the 21st century."
    },
    {
      "type": "heading",
      "text": "Why the Rankings Are Always Slightly Wrong"
    },
    {
      "type": "paragraph",
      "text": "Measuring a river's length is harder than it sounds. Choosing the source is contested: rivers are fed by countless small tributaries, and which one counts as the \"true\" source affects the length by hundreds of kilometres. Choosing the mouth is also contested: where does a river end, exactly, when it spreads into a delta, lagoon, or estuary? GPS-traced expeditions have repeatedly produced different lengths for the same river depending on the methodology used. The numbers in this article are the most defensible current ones, but you will find different figures in reputable sources, and the rankings of rivers 2 through 10 in particular shift slightly between references."
    },
    {
      "type": "heading",
      "text": "What This Means for Your Geography"
    },
    {
      "type": "paragraph",
      "text": "Knowing the longest rivers in the world is one of the best foundations for both classroom geography and satellite-imagery games. Each of these rivers carves a recognisable signature across its continent, and being able to identify a river by its sinuosity, width, sediment load, and surrounding biome is one of the most powerful skills in the geography toolkit. The Nile's sharp green stripe, the Amazon's vast brown channels, the Mississippi's bird-foot delta, the Río de la Plata's sea-like mouth — once these are in your mental atlas, you can place huge swathes of the planet from a single satellite frame."
    }
  ]
};
